import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ImageUpload, MultiImageUpload } from "@/components/ImageUpload";
import VariantBuilder from "@/components/VariantBuilder";

const emptyProduct = {
    name: "",
    description: "",
    price: 0,
    compare_at_price: null,
    category: "Mats",
    image_url: "",
    rating: 4.8,
    long_description: "",
    tags: [],
    stock: 50,
    gallery: [],
    category_id: null,
    attribute_ids: [],
    variants: [],
    // Specifications (all optional)
    dimensions_length: "",
    size: "",
    usage: "",
    product_life: "",
    care: "",
    product_weight: "",
    material_type: "",
    benefits: "",
    how_to_use: "",
    dos_and_donts: "",
    faqs: "",
};

export default function ProductEditor({ open = true, onOpenChange, initial, onSaved, page = false }) {
    const isEdit = Boolean(initial?.id);
    const [form, setForm] = useState(emptyProduct);
    const [tagsRaw, setTagsRaw] = useState("");
    const [saving, setSaving] = useState(false);
    const [categories, setCategories] = useState([]);
    const [attributes, setAttributes] = useState([]);

    useEffect(() => {
        if (open) {
            const seed = initial ? { ...emptyProduct, ...initial } : emptyProduct;
            setForm(seed);
            setTagsRaw((seed.tags || []).join(", "));
            api.get("/admin/categories").then(({ data }) => setCategories(data)).catch(() => setCategories([]));
            api.get("/admin/attributes").then(({ data }) => setAttributes(data)).catch(() => setAttributes([]));
        }
    }, [open, initial]);

    const close = () => onOpenChange?.(false);

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm((f) => ({
            ...f,
            [name]:
                name === "price" || name === "rating" || name === "stock"
                    ? Number(value)
                    : name === "compare_at_price"
                        ? (value === "" ? null : Number(value))
                        : value,
        }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                ...form,
                price: Number(form.price),
                compare_at_price:
                    form.compare_at_price === null || form.compare_at_price === undefined || form.compare_at_price === ""
                        ? null
                        : Number(form.compare_at_price),
                rating: Number(form.rating),
                stock: Number(form.stock ?? 0),
                tags: tagsRaw
                    .split(/[,\n]/)
                    .map((s) => s.trim())
                    .filter(Boolean),
                gallery: form.gallery || [],
                category_id: form.category_id || null,
                attribute_ids: form.attribute_ids || [],
                variants: (form.variants || []).map((v) => ({
                    id: v.id,
                    sku: v.sku,
                    attributes: v.attributes || {},
                    price: Number(v.price ?? 0),
                    stock: Number(v.stock ?? 0),
                    image_url: v.image_url || null,
                })),
            };
            if (isEdit) {
                await api.patch(`/admin/products/${initial.id}`, payload);
                toast.success("Product updated");
            } else {
                await api.post("/admin/products", payload);
                toast.success("Product created");
            }
            onSaved?.();
            close();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    const formBody = (
        <form onSubmit={onSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Name</Label>
                            <Input name="name" value={form.name} onChange={onChange} required className="mt-1.5 rounded-xl" data-testid="product-form-name" />
                        </div>
                        <div>
                            <Label>Category</Label>
                            <Select
                                value={form.category_id || "__none__"}
                                onValueChange={(val) => {
                                    const id = val === "__none__" ? null : val;
                                    const cat = categories.find((c) => c.id === id);
                                    setForm((f) => ({ ...f, category_id: id, category: cat ? cat.name : f.category }));
                                }}
                            >
                                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="product-form-category">
                                    <SelectValue placeholder="Select a category" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">— No category —</SelectItem>
                                    {(() => {
                                        // Order categories as a tree: parents first, each followed by its children.
                                        const byParent = new Map();
                                        for (const c of categories) {
                                            const k = c.parent_id || "__root__";
                                            if (!byParent.has(k)) byParent.set(k, []);
                                            byParent.get(k).push(c);
                                        }
                                        const out = [];
                                        const walk = (key, depth) => {
                                            for (const c of (byParent.get(key) || [])) {
                                                out.push({ c, depth });
                                                walk(c.id, depth + 1);
                                            }
                                        };
                                        walk("__root__", 0);
                                        // Append any orphans whose parent was not loaded.
                                        const seen = new Set(out.map((x) => x.c.id));
                                        for (const c of categories) if (!seen.has(c.id)) out.push({ c, depth: 0 });
                                        return out.map(({ c, depth }) => (
                                            <SelectItem key={c.id} value={c.id}>
                                                {"\u00A0".repeat(depth * 4)}{depth > 0 ? "↳ " : ""}{c.name} (GST {c.gst_rate}%)
                                            </SelectItem>
                                        ));
                                    })()}
                                </SelectContent>
                            </Select>
                            {categories.length === 0 && (
                                <p className="text-[11px] text-ink-700 mt-1">No categories yet — create them under the Categories tab.</p>
                            )}
                        </div>
                        <div>
                            <Label>Price (MRP) (₹)</Label>
                            <Input
                                type="number"
                                name="compare_at_price"
                                value={form.compare_at_price ?? ""}
                                onChange={onChange}
                                min={0}
                                step="0.01"
                                placeholder="Original / MRP — leave empty if same as sell price"
                                className="mt-1.5 rounded-xl"
                                data-testid="product-form-compare-at-price"
                            />
                            <p className="text-[11px] text-ink-400 mt-1">Original list price. Shown struck-through when higher than the sell price.</p>
                        </div>
                        <div>
                            <Label>Sell Price (Discounted Price) (₹)</Label>
                            <Input type="number" name="price" value={form.price} onChange={onChange} required min={0} className="mt-1.5 rounded-xl" data-testid="product-form-price" />
                            <p className="text-[11px] text-ink-400 mt-1">Actual price charged to the customer at checkout.</p>
                        </div>
                        <div>
                            <Label>Rating</Label>
                            <Input type="number" name="rating" step="0.1" value={form.rating} onChange={onChange} required min={0} max={5} className="mt-1.5 rounded-xl" />
                        </div>
                        <div>
                            <Label>Stock</Label>
                            <Input type="number" name="stock" value={form.stock} onChange={onChange} required min={0} className="mt-1.5 rounded-xl" data-testid="product-form-stock" />
                        </div>
                    </div>
                    <div>
                        <Label>Feature image</Label>
                        <div className="mt-1.5">
                            <ImageUpload
                                value={form.image_url}
                                onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
                                testIdPrefix="product-feature-image"
                            />
                        </div>
                        <p className="mt-1 text-xs text-ink-700">Primary image shown on cards and as the first gallery image.</p>
                    </div>
                    <div>
                        <Label>Gallery images</Label>
                        <div className="mt-1.5">
                            <MultiImageUpload
                                value={form.gallery || []}
                                onChange={(urls) => setForm((f) => ({ ...f, gallery: urls }))}
                                testIdPrefix="product-gallery-upload"
                            />
                        </div>
                        <p className="mt-1 text-xs text-ink-700">Additional images shown on the product detail page.</p>
                    </div>
                    <div>
                        <Label>Short description</Label>
                        <Textarea
                            name="description"
                            value={form.description || ""}
                            onChange={onChange}
                            rows={2}
                            className="mt-1.5 rounded-xl"
                            data-testid="product-form-description"
                        />
                    </div>
                    <div>
                        <Label>Long description</Label>
                        <Textarea
                            name="long_description"
                            value={form.long_description || ""}
                            onChange={onChange}
                            rows={4}
                            className="mt-1.5 rounded-xl"
                            data-testid="product-form-long-description"
                        />
                    </div>
                    <div>
                        <Label>Tags (comma separated)</Label>
                        <Input
                            value={tagsRaw}
                            onChange={(e) => setTagsRaw(e.target.value)}
                            placeholder="eco-friendly, non-slip, 6mm"
                            className="mt-1.5 rounded-xl"
                            data-testid="product-form-tags"
                        />
                    </div>

                    <div className="rounded-2xl bg-paper-alt p-4 space-y-4" data-testid="product-spec-fields">
                        <div>
                            <Label className="font-heading text-base">Specifications</Label>
                            <p className="text-xs text-ink-700">Optional product details shown on the storefront product page.</p>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <Label>Dimensions — Length (approx.)</Label>
                                <Input
                                    name="dimensions_length"
                                    value={form.dimensions_length || ""}
                                    onChange={onChange}
                                    placeholder="e.g. 183 cm"
                                    className="mt-1.5 rounded-xl"
                                    data-testid="product-form-dimensions-length"
                                />
                            </div>
                            <div>
                                <Label>Size (approx.)</Label>
                                <Input
                                    name="size"
                                    value={form.size || ""}
                                    onChange={onChange}
                                    placeholder="e.g. Standard / 173x61 cm"
                                    className="mt-1.5 rounded-xl"
                                    data-testid="product-form-size"
                                />
                            </div>
                            <div>
                                <Label>Product Weight</Label>
                                <Input
                                    name="product_weight"
                                    value={form.product_weight || ""}
                                    onChange={onChange}
                                    placeholder="e.g. 1.2 kg"
                                    className="mt-1.5 rounded-xl"
                                    data-testid="product-form-product-weight"
                                />
                            </div>
                            <div>
                                <Label>Material Type</Label>
                                <Input
                                    name="material_type"
                                    value={form.material_type || ""}
                                    onChange={onChange}
                                    placeholder="e.g. Natural rubber, cork"
                                    className="mt-1.5 rounded-xl"
                                    data-testid="product-form-material-type"
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Product Life</Label>
                                <Input
                                    name="product_life"
                                    value={form.product_life || ""}
                                    onChange={onChange}
                                    placeholder="e.g. 3–5 years with daily use"
                                    className="mt-1.5 rounded-xl"
                                    data-testid="product-form-product-life"
                                />
                            </div>
                        </div>
                        <div>
                            <Label>Usage</Label>
                            <Textarea
                                name="usage"
                                value={form.usage || ""}
                                onChange={onChange}
                                rows={2}
                                placeholder="Where and when this product is meant to be used."
                                className="mt-1.5 rounded-xl"
                                data-testid="product-form-usage"
                            />
                        </div>
                        <div>
                            <Label>Care</Label>
                            <Textarea
                                name="care"
                                value={form.care || ""}
                                onChange={onChange}
                                rows={2}
                                placeholder="Cleaning, storage and maintenance tips."
                                className="mt-1.5 rounded-xl"
                                data-testid="product-form-care"
                            />
                        </div>
                        <div>
                            <Label>Benefits</Label>
                            <Textarea
                                name="benefits"
                                value={form.benefits || ""}
                                onChange={onChange}
                                rows={3}
                                placeholder="One key benefit per line."
                                className="mt-1.5 rounded-xl"
                                data-testid="product-form-benefits"
                            />
                        </div>
                        <div>
                            <Label>How to Use it?</Label>
                            <Textarea
                                name="how_to_use"
                                value={form.how_to_use || ""}
                                onChange={onChange}
                                rows={3}
                                placeholder="Step-by-step usage instructions."
                                className="mt-1.5 rounded-xl"
                                data-testid="product-form-how-to-use"
                            />
                        </div>
                        <div>
                            <Label>Dos &amp; Don'ts</Label>
                            <Textarea
                                name="dos_and_donts"
                                value={form.dos_and_donts || ""}
                                onChange={onChange}
                                rows={3}
                                placeholder="Things to do / avoid while using this product."
                                className="mt-1.5 rounded-xl"
                                data-testid="product-form-dos-and-donts"
                            />
                        </div>
                        <div>
                            <Label>FAQs</Label>
                            <Textarea
                                name="faqs"
                                value={form.faqs || ""}
                                onChange={onChange}
                                rows={4}
                                placeholder="Q: Question?&#10;A: Answer.&#10;&#10;Q: …&#10;A: …"
                                className="mt-1.5 rounded-xl"
                                data-testid="product-form-faqs"
                            />
                        </div>
                    </div>

                    <div className="rounded-2xl bg-paper-alt p-4">
                        <Label className="font-heading text-base">Variations</Label>
                        <p className="text-xs text-ink-700 mb-3">Optional — leave empty to sell as a single SKU using the price/stock above.</p>
                        <VariantBuilder
                            attributes={attributes}
                            selectedIds={form.attribute_ids || []}
                            variants={form.variants || []}
                            basePrice={form.price}
                            onChange={({ selectedIds, variants }) =>
                                setForm((f) => ({ ...f, attribute_ids: selectedIds, variants }))
                            }
                        />
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-2">
                        <Button type="button" variant="outline" onClick={close} className="rounded-full">
                            Cancel
                        </Button>
                        <Button type="submit" disabled={saving} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="product-form-save">
                            {saving ? "Saving…" : isEdit ? "Save changes" : "Create product"}
                        </Button>
                    </div>
                </form>
    );

    const title = isEdit ? "Edit product" : "New product";

    if (page) {
        return (
            <div data-testid="product-editor-page-body">
                <h2 className="font-heading text-2xl mb-4">{title}</h2>
                {formBody}
            </div>
        );
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">{title}</DialogTitle>
                </DialogHeader>
                {formBody}
            </DialogContent>
        </Dialog>
    );
}
